import { Injectable } from '@angular/core';
import { TrainingFullModel } from '../models/training/training-full.model';
import {
  PrimaryButtonType,
  SecondaryButtonType,
} from '../models/button-type.model';
import { TrainingStatusModel } from '../models/training/training-status-model';
import { EventMemberModel } from '../models/events/event-member.model';
import { EventMemberStatusModel } from '../models/events/event-member-status.model';
import { TrainingLocationTypeModel } from '../models/training/training-location-type.model';

@Injectable({
  providedIn: 'root',
})
export class TrainingHelperService {
  constructor() {

  }

  public getPrimaryButtonType(
    training: TrainingFullModel,
    isUserTrainer: boolean = false
  ): PrimaryButtonType {

    if (training.status == TrainingStatusModel.Open) {
      return this.getPrimaryForOpen(training, isUserTrainer);
    } 
    
    if (training.status == TrainingStatusModel.WaitingComplete) {
      return this.getPrimaryForWaiting(training, isUserTrainer);
    } 
    
    if (training.status == TrainingStatusModel.Completed) {
      return this.getPrimaryForCompleted(training);
    }

    return PrimaryButtonType.None;
  }

  private getPrimaryForOpen(
    training: TrainingFullModel,
    isUserTrainer: boolean
  ): PrimaryButtonType {

    if (training.isFromSubscribedSource) {
      return PrimaryButtonType.DownloadMemberList;
    }

    if (!training.training.isDeployed) {
      return isUserTrainer
        ? PrimaryButtonType.None
        : PrimaryButtonType.Edit;
    }

    if (
      training.training.trainingLocation.type == 
      TrainingLocationTypeModel.ExternalEvent
    ) {
      return PrimaryButtonType.Edit;
    }

    return isUserTrainer
      ? PrimaryButtonType.DownloadMemberList
      : PrimaryButtonType.AddAttendees;
  }

  private getPrimaryForWaiting(
    training: TrainingFullModel,
    isUserTrainer: boolean
  ): PrimaryButtonType {
    if (!training.training.isDeployed) {
      return isUserTrainer
        ? PrimaryButtonType.None
        : PrimaryButtonType.Edit;
    }

    if (
      training.training.trainingLocation.type == 
      TrainingLocationTypeModel.ExternalEvent
    ) {
      return PrimaryButtonType.Edit;
    }

    return training.isFromSubscribedSource
      ? PrimaryButtonType.DownloadMemberList
      : PrimaryButtonType.CompleteTraining;
  }

  private getPrimaryForCompleted(
    training: TrainingFullModel
  ): PrimaryButtonType {
    if(
      training.training.sendFeedbackLink || 
      training.training.trainingLocation.type == 
        TrainingLocationTypeModel.ExternalEvent
    ) {
      return PrimaryButtonType.ShowFeedback;
    }

    return PrimaryButtonType.DownloadCompletedEventMemberList;
  }

  public getSecondaryButtonTypes(
    training: TrainingFullModel,
    members: EventMemberModel[],
    isUserTrainer: boolean = false
  ): SecondaryButtonType[] {

    const isSubscribed = training.isFromSubscribedSource;
    const isDraft = !training.training.isDeployed;

    const hasAcceptedMembers = members.some(
      m => m.status == EventMemberStatusModel.Accepted
    );

    let buttons: SecondaryButtonType[];

    if (isSubscribed) {
      buttons = this.getSubscribedButtons(training);
    } else if (isUserTrainer) {
      buttons = this.getTrainerButtons(training, isDraft);
    } else {
      buttons = this.getAdminButtons(
        training,
        isDraft,
        hasAcceptedMembers
      );
    }

    return this.filterSecondaryButtonsByLocation(training, buttons);
  }

  private getSubscribedButtons(
    training: TrainingFullModel
  ): SecondaryButtonType[] {

    const buttonTypes: SecondaryButtonType[] = [];

    if (training.status == TrainingStatusModel.Open
      || training.status == TrainingStatusModel.WaitingComplete
    ) {
      buttonTypes.push(
        SecondaryButtonType.DownloadIcs,
        SecondaryButtonType.DownloadESignatureQRCode
      );
    }

    if (training.status == TrainingStatusModel.Completed) {
      buttonTypes.push(SecondaryButtonType.DownloadCompletedEventMemberList);
    }

    buttonTypes.push(SecondaryButtonType.ActivityLog);
    return buttonTypes;
  }

  private getTrainerButtons(
    training: TrainingFullModel,
    isDraft: boolean,
  ): SecondaryButtonType[] {

    const buttons: SecondaryButtonType[] = [];
    
    if((
        training.status == TrainingStatusModel.Open
        || training.status == TrainingStatusModel.WaitingComplete
      ) && isDraft
    ) {
      buttons.push(SecondaryButtonType.ActivityLog);
      return buttons;
    }

    if (training.status == TrainingStatusModel.Open) {
      buttons.push(
        SecondaryButtonType.AddAttendees,
        SecondaryButtonType.CopyLink,
        SecondaryButtonType.DownloadIcs,
        SecondaryButtonType.SendInfo,
        SecondaryButtonType.DownloadESignatureQRCode,
        SecondaryButtonType.CopyFeedbackLink
      );
    }

    if (training.status == TrainingStatusModel.WaitingComplete) {
      buttons.push(
        SecondaryButtonType.CopyLink,
        SecondaryButtonType.DownloadIcs,
        SecondaryButtonType.SendInfo,
        SecondaryButtonType.DownloadESignatureQRCode,
        SecondaryButtonType.CopyFeedbackLink
      );
    }

    if (training.status == TrainingStatusModel.Completed) {
      buttons.push(
        SecondaryButtonType.DownloadCompletedEventMemberList,
        SecondaryButtonType.CopyFeedbackLink
      );
    }

    buttons.push(SecondaryButtonType.ActivityLog);
    return buttons;
  }

  private getAdminButtons(
    training: TrainingFullModel,
    isDraft: boolean,
    hasAcceptedMembers: boolean
  ): SecondaryButtonType[] {

    const buttons: SecondaryButtonType[] = [];

    if((
        training.status == TrainingStatusModel.Open
        || training.status == TrainingStatusModel.WaitingComplete
      ) && isDraft
    ) {

      if (!hasAcceptedMembers) {
        buttons.push(SecondaryButtonType.Delete);
      }

      buttons.push(SecondaryButtonType.ActivityLog);

      return buttons;
    }

    if (training.status == TrainingStatusModel.Open) {
      buttons.push(
        SecondaryButtonType.CopyLink,
        SecondaryButtonType.DownloadMemberList,
        SecondaryButtonType.DownloadIcs,
        SecondaryButtonType.SendInfo,
        SecondaryButtonType.Edit,
        SecondaryButtonType.DownloadESignatureQRCode,
        SecondaryButtonType.CopyFeedbackLink
      );

      if (!hasAcceptedMembers) {
        buttons.push(SecondaryButtonType.Delete);
      } else {
        buttons.push(SecondaryButtonType.Cancel);
      }
    }

    if (training.status == TrainingStatusModel.WaitingComplete) {
      buttons.push(
        SecondaryButtonType.CopyLink,
        SecondaryButtonType.DownloadMemberList,
        SecondaryButtonType.DownloadIcs,
        SecondaryButtonType.SendInfo,
        SecondaryButtonType.Edit,
        SecondaryButtonType.Delete,
        SecondaryButtonType.DownloadESignatureQRCode,
        SecondaryButtonType.CopyFeedbackLink
      );
    }

    if (training.status == TrainingStatusModel.Completed) {
      buttons.push(
        SecondaryButtonType.DownloadCompletedEventMemberList,
        SecondaryButtonType.CopyFeedbackLink
      );

      if (!hasAcceptedMembers) {
        buttons.push(SecondaryButtonType.Delete);
      }
    }

    buttons.push(SecondaryButtonType.ActivityLog);
    return buttons;
  }

  private filterSecondaryButtonsByLocation(
    training: TrainingFullModel,
    buttons: SecondaryButtonType[]
  ): SecondaryButtonType[] {
    if (
      training.training.trainingLocation.type != 
      TrainingLocationTypeModel.ExternalEvent
    ) {
      return buttons;
    }

    return buttons.filter(button =>
      button != SecondaryButtonType.AddAttendees &&
      button != SecondaryButtonType.DownloadMemberList &&
      button != SecondaryButtonType.DownloadESignatureQRCode &&
      button != SecondaryButtonType.SendInfo &&
      button != SecondaryButtonType.DownloadCompletedEventMemberList &&
      button != SecondaryButtonType.Edit
    );
  }
}
